import classnames from "classnames";
import { ReactNode, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import { SortableItemContext, useSortableContext } from "./sortable-list";
import styles from "./sortable-list.module.css";

interface DragItem {
  index: number;
}

interface SortableItemProps {
  index: number;
  children: ReactNode;
  className?: string;
}

const SortableItem = ({ index, children, className }: SortableItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const sortableContext = useSortableContext();

  // Use groupId to create unique item type for nested sortables
  const itemType = sortableContext
    ? `SORTABLE_ITEM_${sortableContext.groupId}`
    : "SORTABLE_ITEM";

  const [{ isDragging }, drag, preview] = useDrag({
    type: itemType,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => !!sortableContext,
    end: () => {
      sortableContext?.onReorderEnd?.();
    },
  });

  const [{ isOver }, drop] = useDrop({
    accept: itemType,
    hover: (item: DragItem, monitor) => {
      if (!ref.current || !sortableContext) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the item's height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      sortableContext.onReorder(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Connect preview and drop to the container
  preview(drop(ref));

  const itemClassName = classnames(styles.item, className, {
    [styles.dragging]: isDragging,
    [styles.over]: isOver,
  });

  return (
    <SortableItemContext.Provider value={{ dragRef: drag }}>
      <div ref={ref} className={itemClassName}>
        {children}
      </div>
    </SortableItemContext.Provider>
  );
};

export default SortableItem;
